import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ShoppingBag, Tag, CheckCircle2, ChevronRight, Sparkles } from 'lucide-react';
import { timeAgo } from '../utils/timeAgo';

const fmt = (n, fuzzy) => fuzzy
  ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })} FUZZY`
  : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP`;

export default function CardShop() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [recentlySold, setRecentlySold] = useState([]);

  const fetchListings = useCallback(() => {
    axios.get(`${API}/shop/listings`).then(r => {
      setListings(r.data.listings || []);
      setRecentlySold(r.data.recently_sold || []);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchListings(); }, [fetchListings]);

  if (listings.length === 0 && recentlySold.length === 0) {
    return (
      <section className="px-4 pt-5 pb-2">
        <div
          data-testid="card-shop-empty-cta"
          onClick={() => navigate('/shop')}
          className="rounded-2xl bg-gradient-to-r from-cyan-900/15 to-slate-800/60 border border-cyan-500/20 cursor-pointer active:scale-[0.98] transition-all duration-150 p-4 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 data-testid="card-shop-heading" className="text-base font-bold text-white leading-tight">Card Shop</h3>
            <p className="text-[11px] text-slate-500">Buy & sell graded cards · escrow-protected · approved hosts list instantly</p>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-1.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex-shrink-0 flex items-center gap-1">
            Open <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </section>
    );
  }

  const preview = listings.slice(0, 3);

  return (
    <section className="px-4 pt-5 pb-2">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <ShoppingBag className="w-4.5 h-4.5 text-cyan-400" />
          </div>
          <div>
            <h3 data-testid="card-shop-heading" className="text-base font-bold text-white leading-tight">Card Shop</h3>
            <p className="text-[11px] text-slate-500">Set-price cards · bulk discounts · ships worldwide</p>
          </div>
        </div>
        {listings.length > 0 && (
          <button
            data-testid="card-shop-browse-all"
            onClick={() => navigate('/shop')}
            className="text-[11px] font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-3 py-1.5 flex items-center gap-1"
          >
            Browse all {listings.length} <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="space-y-2.5">
        {preview.map((l) => (
          <div
            key={l.listing_id}
            data-testid={`shop-listing-${l.listing_id.slice(0, 8)}`}
            onClick={() => navigate('/shop')}
            className="rounded-2xl bg-gradient-to-r from-cyan-900/15 to-slate-800/60 border border-cyan-500/20 cursor-pointer active:scale-[0.98] transition-all duration-150 p-3.5 flex items-center gap-3"
          >
            {l.photo_urls?.[0] ? (
              <img src={l.photo_urls[0]} alt={l.title} className="w-14 h-14 rounded-lg object-cover border border-slate-700 flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                <Tag className="w-5 h-5 text-slate-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-[14px] leading-tight truncate flex items-center gap-1.5">
                {l.title}
                {l.is_limited && <Sparkles className="w-3 h-3 text-amber-400 flex-shrink-0" />}
              </p>
              {(l.grade || l.condition) && (
                <p className="text-[10px] text-slate-400 mt-0.5 truncate">{[l.grade, l.condition].filter(Boolean).join(' · ')}</p>
              )}
              <p className="text-[12px] mt-1">
                <span className="text-cyan-300 font-bold">{fmt(l.price_xrp, false)}</span>
                {(l.quantity || 1) > 1 && <span className="text-slate-500"> · {l.quantity} in stock</span>}
              </p>
            </div>
            <span className="text-[10px] font-bold px-2 py-1 rounded-md bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex-shrink-0">SHOP</span>
          </div>
        ))}
        {recentlySold.slice(0, 2).map((l) => (
          <div
            key={l.listing_id}
            data-testid={`shop-sold-${l.listing_id.slice(0, 8)}`}
            className="relative rounded-2xl bg-slate-800/40 border border-slate-700/50 p-3.5 flex items-center gap-3 overflow-hidden"
          >
            {l.photo_urls?.[0] ? (
              <img src={l.photo_urls[0]} alt={l.title} className="w-14 h-14 rounded-lg object-cover border border-slate-700 flex-shrink-0 grayscale opacity-60" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 opacity-60">
                <Tag className="w-5 h-5 text-slate-600" />
              </div>
            )}
            <div className="flex-1 min-w-0 opacity-70">
              <p className="font-bold text-slate-300 text-[14px] leading-tight truncate">{l.title}</p>
              {(l.grade || l.condition) && (
                <p className="text-[10px] text-slate-500 mt-0.5 truncate">{[l.grade, l.condition].filter(Boolean).join(' · ')}</p>
              )}
              <p className="text-[11px] mt-1 text-slate-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                Sold for <span className="text-emerald-300 font-bold">{fmt(l.sold_price, l.sold_currency === 'FUZZY')}</span>
                {l.sold_at && <span className="text-slate-600">· {timeAgo(l.sold_at)}</span>}
              </p>
            </div>
            <span data-testid="shop-sold-badge" className="text-[10px] font-extrabold tracking-wider px-2 py-1 rounded-md bg-red-500/15 text-red-400 border border-red-500/40 flex-shrink-0 rotate-[-6deg]">SOLD</span>
          </div>
        ))}
      </div>
    </section>
  );
}
